import React, { useEffect, useState, useRef } from "react";
import AdminService from "src/services/admin.services";
import Modal from "./components/Modal";
import BackButton from "src/components/BackButton";
import { AppLayout } from "src/components/layouts";
import { useMutation, useQueryClient } from "react-query";
import RiderDropdown from "src/components/RiderDropDown";

const adminService = new AdminService();

const useAssignAgentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      order_id: string;
      agent_id: string;
      parcel_id?: string;
    }) => adminService.assignAgentToOrder(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["orders"]);
    },
  });
};

const AssignRider: React.FC = () => {
  const [agents, setAgents] = useState<any[]>([]);
  const [orderId, setOrderId] = useState<string>("");
  const [parcelId, setParcelId] = useState<string>("");
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [latitude, setLatitude] = useState<number>(6.13);
  const [longitude, setLongitude] = useState<number>(6.824);
  // Distance in meters (5km = 5000m)
  const [distance, setDistance] = useState<number>(5000);
  const [limit, setLimit] = useState<number>(10);
  const [mealOrders, setMealOrders] = useState<any[]>([]);
  const [parcelOrders, setParcelOrders] = useState<any[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState<"success" | "error">("success");

  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch agents within the specified radius (distance is in meters)
  const searchAgents = async () => {
    try {
      const response = await adminService.searchAgents(
        longitude,
        latitude,
        distance, // Distance in meters (5km = 5000m)
        limit
      );
      if (response && Array.isArray(response)) {
        setAgents(response);
      } else {
        console.error("Failed to fetch agents", response);
      }
    } catch (error: any) {
      console.error(
        "Error fetching agents:",
        error.response ? error.response.data : error.message
      );
    }
  };

  const { mutate: assignAgent } = useAssignAgentMutation();

  const handleAssignAgent = () => {
    if (!selectedAgentId) {
      setModalMessage("Please select a rider to assign.");
      setModalType("error");
      setIsModalOpen(true);
      return;
    }

    assignAgent(
      {
        order_id: orderId,
        agent_id: selectedAgentId,
        parcel_id: parcelId,
      },
      {
        onSuccess: (response) => {
          if (response.message === "updated successfully") {
            setModalMessage("Agent assigned successfully");
            setModalType("success");
            setSelectedAgentId("");
            setOrderId("");
            setParcelId?.("");
            setAgents([]);
            setLatitude(6.13);
            setLongitude(6.824);
            setDistance(5000);
            setLimit(10);
          } else {
            setModalMessage(response.message);
            setModalType("error");
          }
          setIsModalOpen(true);
        },
        onError: (error: any) => {
          const errorMessage =
            error.message ||
            "Only confirmed orders can be accepted for delivery.";
          setModalMessage(errorMessage);
          setModalType("error");
          setIsModalOpen(true);
        },
      }
    );
  };
  // Broadcast delivery request
  const broadcastDeliveryRequest = async () => {
    try {
      const data = {
        latitude,
        longitude,
        order_id: orderId,
        parcel_id: parcelId,
      };
      const response = await adminService.broadcastDeliveryRequest(data);
      if (
        response.message ===
        "processing delivery request. you'll be notified of its status"
      ) {
        setModalMessage(
          "processing delivery request. you'll be notified of its status"
        );
        setModalType("success");
        setParcelId("");
        setOrderId("");
      } else {
        setModalMessage(response.message);
        setModalType("error");
      }
      setIsModalOpen(true);
    } catch (error: any) {
      setModalMessage(error.message);
      setModalType("error");
      setIsModalOpen(true);
    }
  };

  // Scroll to bottom on load or when agents change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [agents]);

  // Handle agent selection
  const handleAgentSelection = (agentId: string) => {
    setSelectedAgentId(agentId);
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const longitude = 8.6753;
        const latitude = 9.082;
        const max_distance = 100000000000000n;

        const parcelRes = await adminService.getLogisticsManagement(
          1,
          longitude,
          latitude,
          max_distance,
          "pending",
          ""
        );
        const mealRes = await adminService.getOrders(
          1,
          100,
          "",
          "confirmed",
          longitude,
          latitude,
          "",
          max_distance
        );
        setMealOrders(
          (mealRes || []).map((item: any) => ({
            ...item,
            _id: item._id, // used internally
            id: `${item._id} - ${item.items?.[0]?.meal?.name || "No Meal"} - ${
              item.restaurant_name || "No Restaurant Name"
            }`, // used for display
          }))
        );

        setParcelOrders(
          (parcelRes || []).map((item: any) => ({
            ...item,
            _id: item._id, // used internally
            id: `${item._id} - ${item.trackingId || "No Tracking ID"} - ${
              item?.pickup_location?.address || "No Address"
            }`, // used for display
          }))
        );
      } catch (err) {
        console.error("Error fetching orders:", err);
      }
    };

    fetchOrders();
  }, []);

  useEffect(() => {
    const fetchCoordinates = async () => {
      if (parcelId) {
        const selectedParcel = parcelOrders.find((p) => p._id === parcelId);
        if (selectedParcel) {
          setLatitude(selectedParcel.pickup_location.latitude);
          setLongitude(selectedParcel.pickup_location.longitude);
        }
      } else if (orderId) {
        const selectedMeal = mealOrders.find((m) => m._id === orderId);
        if (
          selectedMeal &&
          selectedMeal.items &&
          selectedMeal.items[0]?.meal?.restaurant_id
        ) {
          try {
            // Fetch restaurant details using restaurant ID from meal
            const restaurantId = selectedMeal.items[0].meal.restaurant_id;
            const restaurantDetails = await adminService.getRestaurantDetails(
              restaurantId
            );

            // Use restaurant location coordinates
            if (restaurantDetails?.location?.Coordinates) {
              const coordinates = restaurantDetails.location.Coordinates;
              // Coordinates are in [longitude, latitude] format
              setLongitude(coordinates[0]);
              setLatitude(coordinates[1]);
            }
          } catch (error) {
            // Error fetching restaurant details - silently fail
          }
        }
      }
    };

    fetchCoordinates();
  }, [parcelId, orderId, mealOrders, parcelOrders]);
  return (
    <AppLayout>
      <div className="p-4 sm:p-6 h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mb-6 sm:mb-8">
          <BackButton />
          <h2 className="text-2xl sm:text-3xl font-bold text-amber-800">
            Assign Rider to Order
          </h2>
        </div>

        {/* Search Rider Section */}
        <section className="mb-8 sm:mb-10 bg-white p-4 sm:p-6 rounded-xl shadow">
          <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6">
            Search for Riders
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Latitude
              </label>
              <input
                type="number"
                value={latitude}
                onChange={(e) => setLatitude(parseFloat(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Longitude
              </label>
              <input
                type="number"
                value={longitude}
                onChange={(e) => setLongitude(parseFloat(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Distance (5km = 5000m)
              </label>
              <input
                type="number"
                value={distance}
                onChange={(e) => setDistance(parseFloat(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="5000"
                min="100"
                step="100"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter distance in meters. 1000m = 1km
              </p>
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Limit (Number of Riders)
              </label>
              <input
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
          </div>

          {/* Current Search Radius Display */}
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <span className="font-medium">Current Search Radius:</span>{" "}
              {(distance / 1000).toFixed(1)}km ({distance}m)
            </p>
          </div>

          {/* Search Button & Tooltip */}
          <div className="relative group inline-block">
            <button
              onClick={searchAgents}
              disabled={!orderId && !parcelId}
              className={`px-6 py-3 text-sm font-semibold rounded-md transition ${
                !orderId && !parcelId
                  ? "bg-amber-300 cursor-not-allowed text-white"
                  : "bg-amber-500 hover:bg-amber-600 text-white"
              }`}
            >
              Search Riders
            </button>
            {!orderId && !parcelId && (
              <div className="absolute left-1/2 top-full mt-2 -translate-x-1/2 bg-black text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 whitespace-nowrap shadow-lg">
                Select either a Parcel or Meal Order to search
              </div>
            )}
          </div>

          {/* Rider Results */}
          <div
            className="mt-6 max-h-96 overflow-y-auto border border-gray-200 rounded-lg"
            ref={containerRef}
          >
            {agents.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {agents.map((agent) => (
                  <li
                    key={agent.agent[0]._id}
                    className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 gap-3"
                  >
                    <div>
                      <p className="text-base sm:text-lg font-medium text-gray-800">
                        {agent.agent[0].phone}
                      </p>
                      <span className="text-sm text-gray-500">
                        (ID: {agent.agent[0]._id})
                      </span>
                      <p className="text-base sm:text-lg font-medium text-gray-800">
                        {agent.agent[0].first_name ?? ""}{" "}
                        {agent.agent[0].last_name ?? ""}
                      </p>
                    </div>
                    <button
                      className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded w-full sm:w-auto"
                      onClick={() => handleAgentSelection(agent.agent[0]._id)}
                    >
                      Select Rider
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-600 p-4">
                No riders found. Please adjust your search criteria.
              </p>
            )}
          </div>
        </section>

        {/* Assign Rider Section */}
        <section className="mb-8 sm:mb-10 bg-white p-4 sm:p-6 rounded-xl shadow">
          <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6">
            Assign Rider to Order
          </h3>
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Meal Order ID
            </label>
            <RiderDropdown
              options={mealOrders}
              placeholder="Select Meal Order ID"
              selectedId={orderId}
              onChange={(id) => {
                setOrderId(id);
                setParcelId("");
              }}
              emptyMessage="No Meal Available"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Parcel Order ID
            </label>
            <RiderDropdown
              options={parcelOrders}
              placeholder="Select Parcel Order ID"
              selectedId={parcelId}
              onChange={(id) => {
                setParcelId(id);
                setOrderId("");
              }}
              emptyMessage="No Parcel Available"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Selected Rider ID
            </label>
            <input
              type="text"
              value={selectedAgentId}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <button
            disabled={!(selectedAgentId && (orderId || parcelId))}
            onClick={handleAssignAgent}
            className={`mt-2 px-6 py-3 rounded-md text-white font-semibold transition ${
              !(selectedAgentId && (orderId || parcelId))
                ? "bg-amber-300 cursor-not-allowed"
                : "bg-amber-500 hover:bg-amber-600"
            }`}
          >
            Assign Rider
          </button>
        </section>

        {/* Broadcast Section */}
        <section className="bg-white p-4 sm:p-6 rounded-xl shadow mb-10">
          <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6">
            Broadcast Delivery Request
          </h3>
          <button
            onClick={broadcastDeliveryRequest}
            className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded font-semibold transition w-full sm:w-auto"
          >
            Broadcast Delivery
          </button>
        </section>

        {/* Modal */}
        {isModalOpen && (
          <Modal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            type={modalType}
            message={modalMessage}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default AssignRider;
